import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  History as LucideHistory,
  Pencil,
  Star,
  Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import Button from "../../../../../shared/ui/button/Button";
import { blockNoteToPlainText } from "../../../../../shared/utils/blockNote";

import type { TherapeuticPhase } from "./therapeuticPhaseDraft";
import TimelinePhaseAddModal from "./TimelinePhaseAddModal";
import { listPlanSubjectSessions } from "../../../application/planSessions";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";

interface Props {
  patientId: string | null;
  couplePatientId?: string | null;
  type: 'individual' | 'couple';
  phases: TherapeuticPhase[];
  onChange: (phases: TherapeuticPhase[]) => void;
}

export default function TherapeuticPlanAddTimeline({
  patientId,
  couplePatientId,
  type,
  phases,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const [editingPhase, setEditingPhase] = useState<TherapeuticPhase | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sessions, setSessions] = useState<{ value: string; text: string; date: string; hour: string }[]>([]);
  const [expandedPhases, setExpandedPhases] = useState<string[]>([]);

  const togglePhase = (id: string, e: React.MouseEvent) => {
    // Don't toggle if clicking buttons
    if ((e.target as HTMLElement).closest('button')) return;

    setExpandedPhases(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  // 🔹 Load sessions
  useEffect(() => {
    const id = type === 'individual' ? patientId : couplePatientId;
    if (!id) {
      setSessions([]);
      return;
    }

    listPlanSubjectSessions(id, type !== "individual", { offset: 0, limit: 100, exclude_statuses: ["pending", "processing", "failed"] })
      .then((sessions) => {
        const data = sessions || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mapped = data.map((s: any) => {
          const date = new Date(s.date);
          const formattedDate = `${String(date.getDate()).padStart(2, "0")}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}-${date.getFullYear()}`;

          const timeStr = s.hour ? ` ${s.hour}` : "";

          return { value: s.id, text: s.title, date: formattedDate, hour: timeStr };
        });
        setSessions(mapped);
      })
      .catch((err) => {
        console.error("Error fetching sessions:", err);
      });
  }, [patientId, couplePatientId, type]);

  // 🔹 Add new phase
  const openCreatePhase = () => {
    const newPhase: TherapeuticPhase = {
      id: Date.now().toString(),
      title: "",
      description: "",
      notes: "",
      sessions: [],
      done: false,
      order: phases.length + 1,
    };
    setEditingPhase(newPhase);
    setIsCreating(true);
  };

  // 🔹 Save phase
  const savePhase = () => {
    if (!editingPhase) return;

    if (isCreating) {
      onChange([...phases, editingPhase].map((p, i) => ({ ...p, order: i + 1 })));
    } else {
      onChange(
        phases.map((p) => (p.id === editingPhase.id ? editingPhase : p))
          .sort((a, b) => a.order - b.order)
      );
    }
    setEditingPhase(null);
    setIsCreating(false);
  };

  // 🔹 Delete phase
  const deletePhase = (id: string) => {
    onChange(phases.filter((p) => p.id !== id).map((p, i) => ({ ...p, order: i + 1 })));
  };

  // 🔹 Drag and drop
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reordered = Array.from(phases);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    onChange(reordered.map((p, i) => ({ ...p, order: i + 1 })));
  };

  return (
    <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-8">
      <div className="flex justify-between items-center px-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
            <LucideHistory className="w-5 h-5" />
          </div>
          <h3 className="text-xl font-bold text-content-primary">
            {t("therapeuticPlan.timeline")}
          </h3>
        </div>
        <Button
          onClick={openCreatePhase}
          className="shadow-lg shadow-brand-500/20"
        >
          {t("therapeuticPlan.addPhase")}
        </Button>
      </div>

      <div className="relative">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="phases">
            {(provided) => (
              <div
                className="pt-4 space-y-12 relative"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {/* Vertical line with gradient */}
                <div className="absolute left-[18px] lg:left-1/2 top-4 bottom-8 w-1 -translate-x-1/2 bg-gradient-to-b from-brand-500/50 via-brand-500/30 to-brand-500/10 rounded-full dark:from-brand-500/30 dark:to-transparent opacity-40 hidden sm:block" />

                {phases.map((phase, index) => {
                  const isExpanded = expandedPhases.includes(phase.id.toString());
                  const isLast = index === phases.length - 1;

                  return (
                    <Draggable key={phase.id} draggableId={phase.id.toString()} index={index}>
                      {(provided) => (
                        <div
                          className={`relative group w-full flex flex-col ${index % 2 === 0 ? 'lg:items-end' : 'lg:items-start'} pl-12 lg:pl-0`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-0 lg:left-1/2 top-6 -translate-x-1/2 z-10 transition-all duration-300`}>
                            <div className={`w-10 h-10 rounded-full border-4 bg-surface-raised flex items-center justify-center shadow-lg ${phase.done ? 'border-zen-purple bg-zen-purple text-white' :
                              'border-gray-200 text-content-tertiary dark:border-gray-700'
                              }`}>
                              {phase.done ? (
                                <CheckCircle2 className="w-5 h-5" />
                              ) : isLast ? (
                                <Star className="w-5 h-5" />
                              ) : (
                                <Circle className="w-5 h-5" />
                              )}
                            </div>
                          </div>

                          <div
                            onClick={(e) => togglePhase(phase.id.toString(), e)}
                            className={`phase-card p-6 rounded-3xl border bg-white/40 dark:bg-white/[0.02] border-gray-100 dark:border-white/5 transition-all duration-300 cursor-pointer overflow-hidden w-full lg:w-[45%] relative hover:border-brand-400 hover:shadow-lg ${isExpanded ? 'shadow-raised' : 'shadow-theme-xs'}`}
                          >
                            {/* Accent line */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${phase.done ? 'bg-zen-purple' :
                              'bg-gray-200 dark:bg-gray-700'
                              }`} />

                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                  <h3 className="font-semibold text-content-primary truncate">
                                    {phase.title || t("therapeuticPlan.untitledPhase")}
                                  </h3>
                                  {phase.done && (
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-zen-purple/10 text-zen-purple">
                                      {t("common.completed")}
                                    </span>
                                  )}
                                </div>

                                <AnimatePresence>
                                  {isExpanded && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3, ease: "easeInOut" }}
                                      className="overflow-hidden"
                                    >
                                      <p className="text-sm text-content-tertiary leading-relaxed mt-3">
                                        {blockNoteToPlainText(phase.description) || t("therapeuticPlan.noDescription")}
                                      </p>

                                      {phase.notes && (
                                        <div className="mt-4 p-3 rounded-xl bg-gray-50/50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5">
                                          <p className="text-xs font-bold text-content-tertiary uppercase mb-1 tracking-widest">{t("therapeuticPlan.internalNotes")}</p>
                                          <p className="text-[13px] italic text-gray-600 dark:text-gray-300">{blockNoteToPlainText(phase.notes)}</p>
                                        </div>
                                      )}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>

                              <div className="flex gap-2 flex-shrink-0">
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                  <button
                                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingPhase(phase);
                                      setIsCreating(false);
                                    }}
                                    title={t("common.edit")}
                                  >
                                    <Pencil className="w-5 h-5" />
                                  </button>
                                  <button
                                    className="inline-flex items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deletePhase(phase.id);
                                    }}
                                    title={t("common.delete")}
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                                <div className="p-2.5 rounded-xl text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </div>
                              </div>
                            </div>

                            {/* Linked Sessions as chips */}
                            {phase.sessions.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-4">
                                {phase.sessions.map((id) => {
                                  const s = sessions.find((s) => s.value === id);
                                  if (!s) return null;
                                  return (
                                    <div
                                      key={id}
                                      className="flex items-center px-2 py-1 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-300 rounded-lg text-xs font-medium gap-1.5 border border-brand-100 dark:border-brand-500/20"
                                    >
                                      <Calendar className="w-3 h-3 opacity-60" />
                                      <span>{s.date}{s.hour ? ' - ' + s.hour.trim() : ''}</span>
                                      <span className="w-px h-2.5 bg-brand-200 dark:bg-brand-500/30" />
                                      <span className="truncate max-w-[120px]">{s.text}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {editingPhase && (
        <TimelinePhaseAddModal
          phase={editingPhase}
          setPhase={setEditingPhase}
          onSave={savePhase}
          onClose={() => setEditingPhase(null)}
          sessions={sessions}
          isCreating={isCreating}
        />
      )}
    </div>
  );
}

